///
/// EarthBrowser Admin Dashboard - Bearer Token Query
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import {
  type BaseQueryFn,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError
} from '@reduxjs/toolkit/query/react'
import { Mutex } from 'async-mutex'
import { type RootState } from '../store'
import { clearUser } from '../userSlice'

const mutex = new Mutex()
let isRefreshing = false
let lastRefreshAttempt = 0
const REFRESH_COOLDOWN_MS = 5000
const cAuthRefreshURL = '/api/auth/refresh'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.earthbrowser.com'

export const bearerTokenQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  if (isRefreshing) {
    await mutex.waitForUnlock()
  }

  let result = await accessTokenQuery(args, api, extraOptions)

  const error = result.error
  const isSignedIn = (api.getState() as RootState).user.userProfile !== undefined
  if (error?.status === 401 && isSignedIn) {
    const now = Date.now()
    const timeSinceLastRefresh = now - lastRefreshAttempt

    if (timeSinceLastRefresh > REFRESH_COOLDOWN_MS) {
      if (!mutex.isLocked()) {
        const release = await mutex.acquire()
        isRefreshing = true

        try {
          lastRefreshAttempt = now
          const refreshResult = await refreshTokenQuery(cAuthRefreshURL, api, extraOptions)

          if (!refreshResult.data) {
            api.dispatch(clearUser())
          }
        } finally {
          isRefreshing = false
          release()
        }
      } else {
        await mutex.waitForUnlock()
        result = await accessTokenQuery(args, api, extraOptions)
      }
    }
  }

  return result
}

const accessTokenQuery = fetchBaseQuery({
  baseUrl: API_URL,
  credentials: 'include',
})

const refreshTokenQuery = fetchBaseQuery({
  baseUrl: API_URL,
  credentials: 'include',
})

